import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useCITranslate } from '../../i18n/customerIdentity';
import {
  CIConfirmPopup,
  CIDrawer,
  CIInlineAlert,
} from './CustomerIdentityShared';
import CIEditHistory from './CIEditHistory';
import EntitySettingsPanel from './EntitySettingsPanel';
import {
  createCustomerAddress,
  deleteCustomerAddress,
  getAddressHistory,
  listCustomerAddresses,
  updateCustomerAddress,
} from '../../services/customerIdentityApi';
import { reindexCustomerDirectoryRows } from '../../services/customerImportApi';

const EMPTY = {
  label: '',
  contact_name: '',
  company: '',
  phone: '',
  external_shipto_id: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state_province: '',
  postal_code: '',
  country_code: 'US',
  is_default_shipping: false,
  is_default_billing: false,
};

const errorMessage = (err) => ({ kind: 'danger', error: err });
const successMessage = (i18nKey) => ({ kind: 'success', i18nKey });

export default function CustomerAddressesTab({ customer, setMessage }) {
  const t = useCITranslate();

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [drawerMessage, setDrawerMessage] = useState(null);
  const settingsRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCustomerAddresses(customer.id);
      setAddresses(data || []);
    } catch (err) {
      setMessage({
        kind: 'danger',
        i18nKey: 'addresses.couldNotLoad',
        error: err,
      });
    } finally {
      setLoading(false);
    }
  }, [customer.id, setMessage]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setForm({ ...EMPTY, ...(editing || {}) });
    setDrawerMessage(null);
  }, [editing, creating]);

  const setField = (key) => (e) => {
    setForm((prev) => ({
      ...prev,
      [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value,
    }));
    if (drawerMessage?.kind === 'danger') setDrawerMessage(null);
  };

  const closeDrawer = () => {
    setCreating(false);
    setEditing(null);
    setDrawerMessage(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setDrawerMessage(null);
    try {
      let saved;
      if (editing) {
        saved = await updateCustomerAddress(editing.id, form);
      } else {
        saved = await createCustomerAddress({ ...form, customer_id: customer.id });
      }
      try {
        await settingsRef.current?.flush(saved?.id || editing?.id);
      } catch (settingsErr) {
        setDrawerMessage(errorMessage(settingsErr));
        await load();
        return;
      }
      // Real-time reindex so this ship-to is searchable immediately.
      // Fire-and-forget -- never throws, never blocks the save.
      reindexCustomerDirectoryRows('shipto', [saved?.id || editing?.id]);
      closeDrawer();
      await load();
      setMessage(successMessage('addresses.saved'));
    } catch (err) {
      setDrawerMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteCustomerAddress(pendingDelete.id);
      setPendingDelete(null);
      await load();
      setMessage(successMessage('addresses.removed'));
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const drawerTitle = editing ? t('addresses.editAddress') : t('addresses.newAddress');

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="m-0">{t('addresses.title')}</h6>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={() => setCreating(true)}
        >
          <i className="bi bi-plus-lg me-1" /> {t('addresses.addAddress')}
        </button>
      </div>
      <div className="table-responsive">
        <table className="table m-0">
          <thead>
            <tr>
              <th>{t('addresses.columns.label')}</th>
              <th className="text-muted small">{t('addresses.fields.externalShiptoId')}</th>
              <th>{t('addresses.columns.address')}</th>
              <th>{t('addresses.columns.city')}</th>
              <th>{t('addresses.columns.country')}</th>
              <th>{t('addresses.columns.defaults')}</th>
              <th className="text-end">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="text-muted py-3">
                  {t('common.loading')}
                </td>
              </tr>
            ) : null}
            {addresses.length === 0 && !loading ? (
              <tr>
                <td colSpan={7} className="text-center text-muted py-4">
                  {t('addresses.empty')}
                </td>
              </tr>
            ) : null}
            {addresses.map((a) => (
              <tr key={a.id}>
                <td>{a.label || a.contact_name || '—'}</td>
                <td className="small text-muted">{a.external_shipto_id || '—'}</td>
                <td>{[a.address_line1, a.address_line2].filter(Boolean).join(', ')}</td>
                <td>
                  {a.city}
                  {a.state_province ? `, ${a.state_province}` : ''}
                </td>
                <td>{a.country_code}</td>
                <td>
                  {a.is_default_shipping ? (
                    <span className="badge bg-success me-1">
                      {t('addresses.badges.ship')}
                    </span>
                  ) : null}
                  {a.is_default_billing ? (
                    <span className="badge bg-info">
                      {t('addresses.badges.bill')}
                    </span>
                  ) : null}
                </td>
                <td className="text-end">
                  <div className="btn-group btn-group-sm">
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => setEditing(a)}
                    >
                      {t('common.edit')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => setPendingDelete(a)}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CIDrawer
        open={creating || Boolean(editing)}
        title={drawerTitle}
        onClose={closeDrawer}
      >
        <form onSubmit={submit} noValidate>
          <CIInlineAlert
            message={drawerMessage}
            onDismiss={() => setDrawerMessage(null)}
          />
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label className="form-label">{t('addresses.fields.label')}</label>
              <input
                className="form-control"
                value={form.label || ''}
                onChange={setField('label')}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('addresses.fields.contactName')}</label>
              <input
                className="form-control"
                value={form.contact_name || ''}
                onChange={setField('contact_name')}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('addresses.fields.company')}</label>
              <input
                className="form-control"
                value={form.company || ''}
                onChange={setField('company')}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('addresses.fields.phone')}</label>
              <input
                className="form-control"
                value={form.phone || ''}
                onChange={setField('phone')}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('addresses.fields.externalShiptoId')}</label>
              {editing ? (
                <p className="form-control-plaintext text-muted small mb-0">
                  {form.external_shipto_id || '—'}
                </p>
              ) : (
                <input
                  className="form-control"
                  value={form.external_shipto_id || ''}
                  onChange={setField('external_shipto_id')}
                />
              )}
            </div>
            <div className="col-12">
              <label className="form-label">{t('addresses.fields.addressLine1')}</label>
              <input
                className="form-control"
                value={form.address_line1 || ''}
                onChange={setField('address_line1')}
                required
              />
            </div>
            <div className="col-12">
              <label className="form-label">{t('addresses.fields.addressLine2')}</label>
              <input
                className="form-control"
                value={form.address_line2 || ''}
                onChange={setField('address_line2')}
              />
            </div>
            <div className="col-12 col-md-6">
              <label className="form-label">{t('addresses.fields.city')}</label>
              <input
                className="form-control"
                value={form.city || ''}
                onChange={setField('city')}
                required
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">
                {t('addresses.fields.stateProvince')}
              </label>
              <input
                className="form-control"
                value={form.state_province || ''}
                onChange={setField('state_province')}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">{t('addresses.fields.postalCode')}</label>
              <input
                className="form-control"
                value={form.postal_code || ''}
                onChange={setField('postal_code')}
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label">{t('addresses.fields.country')}</label>
              <input
                className="form-control"
                value={form.country_code || 'US'}
                onChange={setField('country_code')}
              />
            </div>
            <div className="col-12 col-md-6 d-flex align-items-center">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="addr_default_ship"
                  checked={!!form.is_default_shipping}
                  onChange={setField('is_default_shipping')}
                />
                <label className="form-check-label" htmlFor="addr_default_ship">
                  {t('addresses.fields.defaultShipping')}
                </label>
              </div>
            </div>
            <div className="col-12 col-md-6 d-flex align-items-center">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="addr_default_bill"
                  checked={!!form.is_default_billing}
                  onChange={setField('is_default_billing')}
                />
                <label className="form-check-label" htmlFor="addr_default_bill">
                  {t('addresses.fields.defaultBilling')}
                </label>
              </div>
            </div>
          </div>

          <EntitySettingsPanel
            ref={settingsRef}
            scope="address"
            scopeId={editing?.id || null}
            contextSiteId={customer.site_id}
            contextCustomerId={customer.id}
            contextAddressId={editing?.id || null}
            defaultExpanded={Boolean(editing)}
            onError={(err) => setDrawerMessage(errorMessage(err))}
          />

          {editing ? (
            <CIEditHistory
              fetchHistory={() => getAddressHistory(editing.id)}
              updatedAt={editing.updated_at}
              resourceId={editing.id}
            />
          ) : null}

          <div className="d-flex justify-content-end gap-2 mt-3">
            <button type="button" className="btn btn-secondary" onClick={closeDrawer}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? t('common.saving') : t('addresses.saveAddress')}
            </button>
          </div>
        </form>
      </CIDrawer>

      <CIConfirmPopup
        open={Boolean(pendingDelete)}
        title={t('addresses.deleteConfirmTitle')}
        message={t('addresses.deleteConfirm')}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
